import React, { useEffect, useState, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  Plus,
  FileText,
  Filter,
  X,
  Eye,
  Clock,
  CheckCircle,
  XCircle,
  AlertTriangle,
  AlertCircle,
  Building2,
  DollarSign,
  ChevronDown,
  Wallet,
  TrendingUp,
  PieChart,
} from 'lucide-react';
import type { AppDispatch, RootState } from '@/store';
import {
  fetchRequests,
  createRequest,
  setStatusFilter,
  clearFilters,
  clearError,
  fetchBudgetImpact,
  selectBudgetImpactReports,
  selectBudgetImpactLoading,
} from '@/store/slices/resourceIntakeRequestsSlice';
import { fetchRateCards } from '@/store/slices/rateCardsSlice';
import { DataTable } from '@/components/ui';
import {
  GlassCard,
  GlassButton,
  GlassBadge,
  GlassPageHeader,
  SectionReveal,
  MetricCard,
  ProgressBar,
  GlassModal,
  GlassLoadingSpinner,
  GlassAlert,
  GlassSelect,
  CollapsibleMetricsSection,
} from '@/design-system';
import { cn } from '@/utils/cn';
import RequestForm from './components/RequestForm';
import RequestDetailModal from './components/RequestDetailModal';
import type {
  ResourceIntakeRequest,
  RequestStatus,
  RequestType,
  ValidationStatus,
  Column,
} from '@/types';
import { UserRole } from '@/types';
import { formatCurrencyCompact, formatPercent, formatCurrencyFull } from '@/utils/formatters';

const ResourceIntakeRequestsPage: React.FC = () => {
  const dispatch = useDispatch<AppDispatch>();
  const {
    entities: requests,
    filters,
    loading,
    error,
  } = useSelector((state: RootState) => state.resourceIntakeRequests);
  const { user } = useSelector((state: RootState) => state.auth);
  const budgetImpactReports = useSelector(selectBudgetImpactReports);
  const budgetImpactLoading = useSelector(selectBudgetImpactLoading);

  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [isDetailModalOpen, setIsDetailModalOpen] = useState(false);
  const [selectedRequest, setSelectedRequest] = useState<ResourceIntakeRequest | null>(null);
  const [showFilters, setShowFilters] = useState(true);
  const [viewMode, setViewMode] = useState<'my' | 'all'>('my');
  const [showBudgetPanel, setShowBudgetPanel] = useState(true);

  const isAdminOrExecutive = user && (user.role === UserRole.ADMIN || user.role === UserRole.EXECUTIVE);

  useEffect(() => {
    dispatch(fetchRequests({ status: filters.status || undefined }));
    dispatch(fetchRateCards({ year: new Date().getFullYear() }));
  }, [dispatch, filters.status]);

  // Fetch budget impact reports for admin/executive users
  useEffect(() => {
    if (isAdminOrExecutive) {
      dispatch(fetchBudgetImpact());
    }
  }, [dispatch, isAdminOrExecutive]);

  useEffect(() => {
    if (error) {
      const timer = setTimeout(() => {
        dispatch(clearError());
      }, 5000);
      return () => clearTimeout(timer);
    }
  }, [error, dispatch]);

  const handleCreateRequest = async (data: Partial<ResourceIntakeRequest>) => {
    const result = await dispatch(createRequest(data));
    if (createRequest.fulfilled.match(result)) {
      setIsCreateModalOpen(false);
    }
  };

  const handleViewRequest = (request: ResourceIntakeRequest) => {
    setSelectedRequest(request);
    setIsDetailModalOpen(true);
  };

  const hasActiveFilters = filters.status !== null;

  // Filter requests based on view mode
  const filteredRequests = viewMode === 'my'
    ? requests.filter((req) => req.requestedById === user?.id)
    : requests;

  // Summary stats for budget impact
  const budgetSummary = useMemo(() => {
    if (!budgetImpactReports.length) return null;

    const totalLaborBudget = budgetImpactReports.reduce((sum, r) => sum + (r.laborBudget || 0), 0);
    const totalCommitted = budgetImpactReports.reduce((sum, r) => sum + r.totalCommittedCost, 0);
    const totalPending = budgetImpactReports.reduce((sum, r) => sum + r.pendingRequestsCost, 0);
    const totalRemaining = budgetImpactReports.reduce((sum, r) => sum + (r.remainingBudget || 0), 0);

    return {
      totalLaborBudget,
      totalCommitted,
      totalPending,
      totalRemaining,
      utilizationPercent: totalLaborBudget > 0 ? (totalCommitted / totalLaborBudget) * 100 : 0,
    };
  }, [budgetImpactReports]);

  const getStatusBadge = (status: RequestStatus) => {
    const variantMap: Record<RequestStatus, 'warning' | 'success' | 'danger'> = {
      SUBMITTED: 'warning',
      APPROVED: 'success',
      DECLINED: 'danger',
    };
    const icons: Record<RequestStatus, React.ReactNode> = {
      SUBMITTED: <Clock className="w-3 h-3 mr-1" />,
      APPROVED: <CheckCircle className="w-3 h-3 mr-1" />,
      DECLINED: <XCircle className="w-3 h-3 mr-1" />,
    };
    return (
      <GlassBadge variant={variantMap[status]} size="sm">
        {icons[status]}
        {status}
      </GlassBadge>
    );
  };

  const getValidationBadge = (status: ValidationStatus) => {
    const variantMap: Record<ValidationStatus, 'info' | 'success' | 'warning' | 'danger'> = {
      PENDING: 'info',
      VALID: 'success',
      WARNING: 'warning',
      INVALID: 'danger',
    };
    const icons: Record<ValidationStatus, React.ReactNode> = {
      PENDING: <Clock className="w-3 h-3" />,
      VALID: <CheckCircle className="w-3 h-3" />,
      WARNING: <AlertTriangle className="w-3 h-3" />,
      INVALID: <AlertCircle className="w-3 h-3" />,
    };
    return (
      <GlassBadge variant={variantMap[status]} size="sm">
        {icons[status]}
      </GlassBadge>
    );
  };

  const getRequestTypeBadge = (type: RequestType) => {
    const variantMap: Record<RequestType, 'primary' | 'info' | 'default'> = {
      HEADCOUNT: 'primary',
      SOW: 'info',
      T_AND_M: 'default',
    };
    const labels: Record<RequestType, string> = {
      HEADCOUNT: 'Headcount',
      SOW: 'SOW',
      T_AND_M: 'T&M',
    };
    return (
      <GlassBadge variant={variantMap[type]} size="sm">
        {labels[type]}
      </GlassBadge>
    );
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  };

  const columns: Column<ResourceIntakeRequest>[] = [
    {
      key: 'id',
      header: 'Request ID',
      sortable: true,
      render: (value) => (
        <div className="font-mono text-xs text-foreground">
          {(value as string).slice(0, 8).toUpperCase()}
        </div>
      ),
    },
    {
      key: 'requestType',
      header: 'Type',
      sortable: true,
      render: (value) => getRequestTypeBadge(value as RequestType),
    },
    {
      key: 'description',
      header: 'Description',
      render: (value) => (
        <div className="max-w-xs truncate text-sm text-foreground" title={value as string}>
          {value as string}
        </div>
      ),
    },
    {
      key: 'program',
      header: 'Program',
      render: (value: any) =>
        value ? (
          <div className="flex items-center gap-1.5">
            <Building2 className="w-3.5 h-3.5 text-muted-foreground" />
            <span className="text-sm text-foreground">{value.name}</span>
          </div>
        ) : (
          <span className="text-xs text-muted-foreground">-</span>
        ),
    },
    {
      key: 'resourceLevel',
      header: 'Level',
      sortable: true,
      render: (value: any) => (
        <span className="font-mono text-xs text-foreground">
          {value?.code || value || '-'}
        </span>
      ),
    },
    {
      key: 'annualCost',
      header: 'Annual Cost',
      sortable: true,
      render: (value, row) => (
        <div className="text-sm text-foreground font-medium">
          {formatCurrencyFull((value || row.estimatedCost || 0) as number, { fallback: '$0' })}
        </div>
      ),
    },
    {
      key: 'validationStatus',
      header: 'Budget',
      render: (value) => (value ? getValidationBadge(value as ValidationStatus) : '-'),
    },
    ...(viewMode === 'all'
      ? [
          {
            key: 'requestedBy' as keyof ResourceIntakeRequest,
            header: 'Requested By',
            render: (value: any) => (
              <div className="text-sm text-foreground">
                {value ? `${value.firstName} ${value.lastName}` : 'Unknown'}
              </div>
            ),
          },
        ]
      : []),
    {
      key: 'submitDate',
      header: 'Submit Date',
      sortable: true,
      render: (value) => (
        <div className="text-sm text-foreground">{formatDate(value as string)}</div>
      ),
    },
    {
      key: 'status',
      header: 'Status',
      sortable: true,
      render: (value) => getStatusBadge(value as RequestStatus),
    },
  ];

  if (loading && requests.length === 0) {
    return (
      <div className="flex items-center justify-center h-64">
        <GlassLoadingSpinner size="lg" variant="gradient" label="Loading requests..." />
      </div>
    );
  }

  const submittedCount = filteredRequests.filter((r) => r.status === 'SUBMITTED').length;
  const approvedCount = filteredRequests.filter((r) => r.status === 'APPROVED').length;
  const declinedCount = filteredRequests.filter((r) => r.status === 'DECLINED').length;

  return (
    <div className="space-y-6">
      <GlassPageHeader
        title="Resource Intake Requests"
        subtitle="Submit and manage resource requests"
        icon={<FileText className="w-6 h-6" />}
        iconGradient="blue-violet"
        actions={
          <div className="flex items-center gap-2">
            <GlassButton
              variant="secondary"
              size="sm"
              onClick={() => setShowFilters(!showFilters)}
              leftIcon={<Filter className="w-4 h-4" />}
            >
              {showFilters ? 'Hide' : 'Show'} Filters
              {hasActiveFilters && (
                <span className="ml-1 inline-flex items-center justify-center w-5 h-5 text-xs font-medium text-white bg-blue-600 rounded-full">
                  1
                </span>
              )}
            </GlassButton>
            <GlassButton
              variant="primary"
              size="sm"
              onClick={() => setIsCreateModalOpen(true)}
              leftIcon={<Plus className="w-4 h-4" />}
            >
              New Request
            </GlassButton>
          </div>
        }
      />

      {error && (
        <GlassAlert
          variant="error"
          message={error}
          onClose={() => dispatch(clearError())}
        />
      )}

      {/* Budget Impact Panel - For Admin/Executive only */}
      {isAdminOrExecutive && budgetSummary && (
        <SectionReveal delay={0.1}>
          <GlassCard padding="none">
            <button
              className={cn(
                "w-full px-5 py-4 flex items-center justify-between",
                "rounded-t-2xl transition-all duration-200",
                "hover:bg-white/60 dark:hover:bg-slate-800/60",
                "focus:outline-none focus-visible:ring-2 focus-visible:ring-accent-blue focus-visible:ring-offset-2"
              )}
              onClick={() => setShowBudgetPanel(!showBudgetPanel)}
              aria-expanded={showBudgetPanel}
              aria-controls="budget-impact-content"
            >
              <div className="flex items-center gap-3">
                {/* Gradient icon container */}
                <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-blue-500/20 to-violet-500/20 flex items-center justify-center">
                  <DollarSign className="w-5 h-5 text-blue-600 dark:text-blue-400" />
                </div>
                <div className="text-left">
                  <span className="font-semibold text-foreground">Budget Impact Summary</span>
                  <span className="ml-2 text-sm text-muted-foreground/60">
                    {budgetImpactReports.length} programs
                  </span>
                </div>
              </div>
              <ChevronDown
                className={cn(
                  "w-5 h-5 text-muted-foreground transition-transform duration-200",
                  showBudgetPanel && "rotate-180"
                )}
              />
            </button>

            {showBudgetPanel && (
              <div
                id="budget-impact-content"
                role="region"
                aria-label="Budget impact details"
                className="border-t border-glass-border"
              >
              {budgetImpactLoading ? (
                <div className="flex items-center justify-center py-8">
                  <GlassLoadingSpinner size="md" variant="dots" />
                </div>
              ) : (
                <>
                  {/* Summary Stats with MetricCards */}
                  <div className="p-5">
                    <CollapsibleMetricsSection storageKey="intake-requests">
                      <div className="grid grid-cols-2 lg:grid-cols-5 gap-3">
                        <MetricCard
                          title="Total Labor Budget"
                          value={formatCurrencyCompact(budgetSummary.totalLaborBudget)}
                          icon={<Wallet className="w-4 h-4" />}
                          iconColor="blue"
                        />
                        <MetricCard
                          title="Committed"
                          value={formatCurrencyCompact(budgetSummary.totalCommitted)}
                          icon={<CheckCircle className="w-4 h-4" />}
                          iconColor="emerald"
                          progress={budgetSummary.totalLaborBudget > 0
                            ? (budgetSummary.totalCommitted / budgetSummary.totalLaborBudget) * 100
                            : 0}
                        />
                        <MetricCard
                          title="Pending"
                          value={formatCurrencyCompact(budgetSummary.totalPending)}
                          icon={<Clock className="w-4 h-4" />}
                          iconColor="amber"
                        />
                        <MetricCard
                          title="Remaining"
                          value={formatCurrencyCompact(budgetSummary.totalRemaining)}
                          icon={<TrendingUp className="w-4 h-4" />}
                          iconColor={budgetSummary.totalRemaining > 0 ? 'cyan' : 'rose'}
                        />
                        <MetricCard
                          title="Utilization"
                          value={formatPercent(budgetSummary.utilizationPercent, { decimals: 0 })}
                          icon={<PieChart className="w-4 h-4" />}
                          iconColor={
                            budgetSummary.utilizationPercent > 100 ? 'rose' :
                            budgetSummary.utilizationPercent > 80 ? 'amber' : 'emerald'
                          }
                          progress={Math.min(budgetSummary.utilizationPercent, 100)}
                        />
                      </div>
                    </CollapsibleMetricsSection>
                  </div>

                  {/* Programs at Risk */}
                  {budgetImpactReports.filter(r => (r.budgetUtilizationPercent || 0) > 80).length > 0 && (
                    <div className="px-5 pb-5 pt-2 border-t border-glass-border">
                      <div className="flex items-center gap-2 mb-3">
                        <div className="w-6 h-6 rounded-lg bg-amber-100 dark:bg-amber-900/30 flex items-center justify-center">
                          <AlertTriangle className="w-3.5 h-3.5 text-amber-600 dark:text-amber-400" />
                        </div>
                        <span className="text-sm font-semibold text-foreground">
                          Programs at Risk
                        </span>
                        <GlassBadge variant="warning" size="sm">
                          {budgetImpactReports.filter(r => (r.budgetUtilizationPercent || 0) > 80).length}
                        </GlassBadge>
                      </div>

                      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
                        {budgetImpactReports
                          .filter(r => (r.budgetUtilizationPercent || 0) > 80)
                          .sort((a, b) => (b.budgetUtilizationPercent || 0) - (a.budgetUtilizationPercent || 0))
                          .slice(0, 6)
                          .map((report) => {
                            const isOverBudget = (report.budgetUtilizationPercent || 0) > 100;
                            return (
                              <GlassCard
                                key={report.programId}
                                variant="subtle"
                                padding="sm"
                                className={cn(
                                  "border-l-4",
                                  isOverBudget ? "border-l-rose-500" : "border-l-amber-500"
                                )}
                              >
                                <div className="flex items-center justify-between mb-2">
                                  <span className="font-medium text-sm text-foreground truncate">
                                    {report.programName}
                                  </span>
                                  <GlassBadge
                                    variant={isOverBudget ? "danger" : "warning"}
                                    size="sm"
                                  >
                                    {formatPercent(report.budgetUtilizationPercent || 0, { decimals: 0 })}
                                  </GlassBadge>
                                </div>
                                <div className="flex items-center justify-between">
                                  <span className="text-xs text-muted-foreground/60">
                                    {report.pendingRequestsCount} pending
                                  </span>
                                  <ProgressBar
                                    value={Math.min(report.budgetUtilizationPercent || 0, 100)}
                                    color={isOverBudget ? "rose" : "amber"}
                                    size="sm"
                                    className="w-16"
                                  />
                                </div>
                              </GlassCard>
                            );
                          })}
                      </div>
                    </div>
                  )}
                </>
              )}
              </div>
            )}
          </GlassCard>
        </SectionReveal>
      )}

      {/* View Mode Toggle */}
      <SectionReveal delay={0.15}>
        <div className="flex items-center gap-2">
          <GlassButton
            variant={viewMode === 'my' ? 'primary' : 'secondary'}
            size="sm"
            onClick={() => setViewMode('my')}
          >
            My Requests
          </GlassButton>
          {isAdminOrExecutive && (
            <GlassButton
              variant={viewMode === 'all' ? 'primary' : 'secondary'}
              size="sm"
              onClick={() => setViewMode('all')}
            >
              All Requests
            </GlassButton>
          )}
        </div>
      </SectionReveal>

      {showFilters && (
        <SectionReveal delay={0.1}>
          <GlassCard className="p-4">
            <div className="flex items-center gap-4">
              <div className="flex-1">
                <GlassSelect
                  label="Status Filter"
                  value={filters.status || ''}
                  onChange={(value) => dispatch(setStatusFilter((value as RequestStatus) || null))}
                  options={[
                    { value: '', label: 'All Statuses' },
                    { value: 'SUBMITTED', label: 'Submitted' },
                    { value: 'APPROVED', label: 'Approved' },
                    { value: 'DECLINED', label: 'Declined' },
                  ]}
                  fullWidth
                />
              </div>

              <div className="flex-1">
                <div className="text-sm font-medium text-foreground mb-1">
                  Summary ({viewMode === 'my' ? 'My Requests' : 'All Requests'})
                </div>
                <div className="flex items-center gap-4 text-sm">
                  <div>
                    <span className="font-medium text-status-warning">{submittedCount}</span>
                    <span className="text-muted-foreground ml-1">Submitted</span>
                  </div>
                  <div>
                    <span className="font-medium text-status-success">{approvedCount}</span>
                    <span className="text-muted-foreground ml-1">Approved</span>
                  </div>
                  <div>
                    <span className="font-medium text-status-error">{declinedCount}</span>
                    <span className="text-muted-foreground ml-1">Declined</span>
                  </div>
                </div>
              </div>

              {hasActiveFilters && (
                <div className="flex items-end">
                  <GlassButton
                    variant="ghost"
                    size="sm"
                    onClick={() => dispatch(clearFilters())}
                    leftIcon={<X className="w-4 h-4" />}
                  >
                    Clear
                  </GlassButton>
                </div>
              )}
            </div>
          </GlassCard>
        </SectionReveal>
      )}

      <SectionReveal delay={0.2}>
        {!loading && filteredRequests.length === 0 ? (
          <GlassCard>
            <div className="text-center py-12">
              <FileText className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
              <p className="text-muted-foreground text-lg">No resource intake requests found.</p>
              <p className="text-muted-foreground/60 mt-2">Create your first request to get started.</p>
            </div>
          </GlassCard>
        ) : (
          <GlassCard padding="none">
            <div className="max-h-[calc(100vh-350px)] overflow-auto">
              <DataTable
                data={filteredRequests}
                columns={columns}
                searchable
                searchPlaceholder="Search requests..."
                sortable
                pagination
                pageSize={25}
                emptyMessage={viewMode === 'my' ? "You haven't submitted any requests yet" : "No requests found"}
                emptyIcon={<FileText className="w-12 h-12 text-muted-foreground" />}
                actions={(row) => (
                  <div className="flex items-center gap-2">
                    <button
                      onClick={() => handleViewRequest(row)}
                      className="p-1.5 rounded-md text-muted-foreground hover:text-accent-blue hover:bg-accent-blue/10 transition-all duration-200"
                      title="View details"
                      aria-label="View request details"
                    >
                      <Eye className="w-4 h-4" />
                    </button>
                  </div>
                )}
              />
            </div>
          </GlassCard>
        )}
      </SectionReveal>

      {/* Create Request Modal */}
      <GlassModal
        isOpen={isCreateModalOpen}
        onClose={() => setIsCreateModalOpen(false)}
        title="New Resource Intake Request"
        subtitle="Submit a new headcount, SOW, or T&M request"
        icon={<Plus className="w-5 h-5" />}
        iconGradient="blue-violet"
        size="lg"
      >
        <RequestForm
          onSubmit={handleCreateRequest}
          onCancel={() => setIsCreateModalOpen(false)}
          isLoading={loading}
        />
      </GlassModal>

      {/* Request Detail Modal */}
      <RequestDetailModal
        isOpen={isDetailModalOpen}
        onClose={() => {
          setIsDetailModalOpen(false);
          setSelectedRequest(null);
        }}
        request={selectedRequest}
      />
    </div>
  );
};

export default ResourceIntakeRequestsPage;
